import React, { useState, useEffect } from 'react';
import apiClient from '../../api/apiClient';
import { Icon } from "@iconify/react/dist/iconify.js";
import { format, isWithinInterval, parseISO } from 'date-fns';
import { useWeek } from '../../contexts/WeekContext';
import WeekPicker from '../../components/calendar/WeekPicker';

// Chart Components
import ApiHealthChart from '../../components/monitoring/ApiHealthChart';
import CpuUsageChart from '../../components/monitoring/CpuUsageChart';
import DependencyChart from '../../components/monitoring/DependencyChart';
import TopBlockedIpsChart from '../../components/monitoring/TopBlockedIpsChart';
import TopAttackedUrlsChart from '../../components/monitoring/TopAttackedUrlsChart';
import AttackTypeBreakdown from '../../components/monitoring/AttackTypeBreakdown';
import AttackTypeDetailsTable from '../../components/monitoring/AttackTypeDetailsTable';

const AdminAlerts = () => {
  const { dateRange } = useWeek();
  const [healthData, setHealthData] = useState({
    summary: [],
    timeSeriesData: {
      api: [],
      cpu: [],
      dependencies: {
        azureBlob: [],
        auth0: []
      }
    },
    errorDetails: {
      clientErrors: [],
      serverErrors: [],
      summary: {
        totalClientErrors: 0,
        totalServerErrors: 0
      }
    }
  });
  const [wafMetrics, setWafMetrics] = useState({
    blockedIps: [],
    attackedUrls: [],
    attackTypes: [
      /* Example structure:
      { 
        type: "Anomaly Score Violation", 
        count: 0,
        details: [
          {
            day: "2023-06-15",
            message: "Greater and Equal to Tx:inbound_anomaly_score_threshold at TX:anomaly_score",
            ruleId: "949110",
            timestamp: "2023-06-15T14:23:45.000Z"
          }
        ]
      }
      */
    ],
    timeSeriesData: {
      dailyActivity: [],
      topIpsDaily: {},
      topUrlsDaily: {},
      attackTypesByDay: []
    },
    summary: {
      totalBlockedIps: 0,
      uniqueBlockedIps: 0,
      uniqueAttackedUrls: 0,
      uniqueAttackTypes: 0
    }
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isWafLoading, setIsWafLoading] = useState(true);
  const [error, setError] = useState(null);
  const [wafError, setWafError] = useState(null);

  useEffect(() => {
    const fetchHealthData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        // The endpoint is not customer-specific, but the hook handles admin auth
        const startDate = format(dateRange.start, 'yyyy-MM-dd');
        const endDate = format(dateRange.end, 'yyyy-MM-dd');
        const response = await apiClient.get(`/api/system-health?startDate=${startDate}&endDate=${endDate}`);
        setHealthData(response.data);
      } catch (err) {
        setError(err.message || 'Failed to fetch system health.');
        setHealthData({
          summary: [],
          timeSeriesData: {
            api: [],
            cpu: [],
            dependencies: {
              azureBlob: [],
              auth0: []
            }
          },
          errorDetails: {
            clientErrors: [],
            serverErrors: [],
            summary: {
              totalClientErrors: 0,
              totalServerErrors: 0
            }
          }
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchHealthData();
  }, [dateRange]);
  
  useEffect(() => {
    const fetchWafMetrics = async () => {
      try {
        setIsWafLoading(true);
        setWafError(null);
        const startDate = format(dateRange.start, 'yyyy-MM-dd');
        const endDate = format(dateRange.end, 'yyyy-MM-dd');
        const response = await apiClient.get(`/api/security/waf-metrics?startDate=${startDate}&endDate=${endDate}`);
        setWafMetrics(response.data);
      } catch (err) {
        setWafError(err.message || 'Failed to fetch WAF metrics.');
        setWafMetrics({
          blockedIps: [],
          attackedUrls: [],
          attackTypes: [],
          timeSeriesData: {
            dailyActivity: [],
            topIpsDaily: {},
            topUrlsDaily: {}
          },
          summary: {
            totalBlockedIps: 0,
            uniqueBlockedIps: 0,
            uniqueAttackedUrls: 0,
            uniqueAttackTypes: 0
          }
        });
      } finally {
        setIsWafLoading(false);
      }
    };

    fetchWafMetrics();
  }, [dateRange]);

  // Filter data based on selected date range
  const filterDataByDateRange = (data) => {
    if (!data || !Array.isArray(data) || !dateRange.start || !dateRange.end) {
      return data;
    }

    return data.filter(item => {
      const itemDate = parseISO(item.day);
      return isWithinInterval(itemDate, { start: dateRange.start, end: dateRange.end });
    });
  };
  
  // Filter WAF data based on selected date range
  const filterWafDataByDateRange = (data) => {
    if (!data || !Array.isArray(data) || !dateRange.start || !dateRange.end) {
      return data;
    }
    
    // For each item in the data array, filter its daily data based on date range
    return data.map(item => {
      if (!item.daily || !Array.isArray(item.daily)) {
        return item;
      }
      
      const filteredDaily = item.daily.filter(dailyItem => {
        const itemDate = parseISO(dailyItem.day);
        return isWithinInterval(itemDate, { start: dateRange.start, end: dateRange.end });
      });
      
      return { ...item, daily: filteredDaily };
    });
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'healthy':
        return 'text-green-500';
      case 'degraded':
        return 'text-orange-500';
      case 'unhealthy':
        return 'text-error';
      default:
        return 'text-gray-500';
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-100 text-red-800 rounded-md">
        Error: {error}
      </div>
    );
  }

  const renderWafSection = () => {
    if (isWafLoading) {
      return (
        <div className="flex justify-center items-center h-32 mt-4">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      );
    }

    if (wafError) {
      return (
        <div className="p-4 bg-red-100 text-red-800 rounded-md mt-4">
          Error loading WAF metrics: {wafError}
        </div>
      );
    }

    return (
      <div className="w-full flex flex-col gap-4">
        <div className="subheader-textbox">
          {/* <Icon 
            icon="material-symbols:security" 
            className="text-primary responsive-icon-sm"
          /> */}
          Web Application Firewall (WAF) Metrics
        </div>
        
        <div className="flex flex-col gap-4 w-full">
          {/* WAF Charts */}
          <div className="chart-container-responsive w-full">
            <TopBlockedIpsChart 
              data={filterWafDataByDateRange(wafMetrics.blockedIps)} 
            />
          </div>
          <div className="chart-container-responsive w-full">
            <TopAttackedUrlsChart 
              data={filterWafDataByDateRange(wafMetrics.attackedUrls)} 
            />
          </div>
          
          {/* Attack Type Breakdown Chart */}
          <div className="chart-container-responsive w-full">
            <AttackTypeBreakdown 
              attackTypesByDay={filterWafDataByDateRange(wafMetrics.timeSeriesData.attackTypesByDay)}
              attackTypes={wafMetrics.attackTypes}
            />
          </div>
          
          {/* Attack Type Breakdown Table with Details */}
          <div className="button-container rounded-md p-4">
            <AttackTypeDetailsTable attackTypes={wafMetrics.attackTypes} />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="responsive-page-layout scrollable w-full">
      <div className="insights-title flex flex-row justify-between items-center w-full z-10">
        <div className="header-textbox">
          <Icon icon="lucide:monitor-cog" className="header-icon"></Icon>
          Monitoring
        </div>
      </div>
      
      <div className="flex flex-col gap-4 w-full">
        <div className="subheader-textbox">
          {/* <Icon icon="material-symbols:monitoring" className="responsive-icon-sm"></Icon> */}
          System Health
        </div>
        {/* System Health Charts */}
        <div className="chart-container-responsive w-full">
          {healthData.summary.length > 0 && (
            <ApiHealthChart 
              data={filterDataByDateRange(healthData.timeSeriesData.api)} 
              errorDetails={{
                serverErrors: filterDataByDateRange(healthData.errorDetails?.serverErrors || []),
                clientErrors: filterDataByDateRange(healthData.errorDetails?.clientErrors || []),
                summary: healthData.errorDetails?.summary || {}
              }}
              summary={healthData.summary.find(item => item.component === 'Backend API') || {}} 
            />
          )}
        </div>
        <div className="chart-insights-responsive w-full">
          <div className="chart-container-responsive">
            {healthData.summary.length > 0 && (
              <CpuUsageChart 
                data={filterDataByDateRange(healthData.timeSeriesData.cpu)} 
                summary={healthData.summary.find(item => item.component === 'CPU Usage') || {}} 
              />
            )}
          </div>
          <div className="chart-container-responsive">
            {healthData.summary.length > 0 && (
              <DependencyChart 
                azureData={filterDataByDateRange(healthData.timeSeriesData.dependencies.azureBlob)} 
                auth0Data={filterDataByDateRange(healthData.timeSeriesData.dependencies.auth0)} 
                summary={healthData.summary.filter(item => 
                  item.component === 'Azure Blob Storage' || 
                  item.component === 'Auth0 Service'
                )}
              />
            )}
          </div>
        </div>
      </div>
      
      {renderWafSection()}
    </div>
  );
};

export default AdminAlerts;